import { useEffect, useRef } from 'react'
import styled from 'styled-components'

interface LogoOrbitProps {
  src: string
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center'
  size?: number
  centerSize?: number
  orbitCount?: number
  ringCount?: number
  color?: string
  opacity?: number
}

const Container = styled.div<{ $position: string; $size: number; $zindex: number }>`
  position: fixed;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  pointer-events: none;
  z-index: ${({ $zindex }) => $zindex};

  ${({ $position }) => {
    switch ($position) {
      case 'bottom-right':
        return 'bottom: 20px; right: 20px;'
      case 'bottom-left':
        return 'bottom: 20px; left: 20px;'
      case 'top-right':
        return 'top: 20px; right: 20px;'
      case 'top-left':
        return 'top: 20px; left: 20px;'
      case 'center':
        return 'top: 50%; left: 50%; transform: translate(-50%, -50%);'
      default:
        return 'bottom: 20px; right: 20px;'
    }
  }}
`

const CenterLogo = styled.img<{ $size: number; $opacity: number; $color: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  opacity: ${({ $opacity }) => $opacity};

  @keyframes breathe {
    0%, 100% { transform: translate(-50%, -50%) scale(1); }
    50% { transform: translate(-50%, -50%) scale(1.05); }
  }
`

const Ring = styled.div<{ $size: number; $delay: number; $color: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border: 1px solid rgba(${({ $color }) => $color}, 0.35);
  border-radius: 50%;
  animation: pulse 3s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;

  @keyframes pulse {
    0%, 100% { opacity: 0.3; transform: translate(-50%, -50%) scale(1); }
    50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.05); }
  }
`

const OrbitLine = styled.div<{ $size: number; $duration: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border: 1px dashed rgba(233, 30, 99, 0.35);
  border-radius: 50%;
  animation: rotate ${({ $duration }) => $duration}s linear infinite;

  @keyframes rotate {
    from { transform: translate(-50%, -50%) rotate(0deg); }
    to { transform: translate(-50%, -50%) rotate(360deg); }
  }
`

const Satellite = styled.img<{ $orbitSize: number; $duration: number; $delay: number; $size: number }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  animation: orbit ${({ $duration }) => $duration}s linear infinite;
  animation-delay: ${({ $delay }) => $delay}s;

  @keyframes orbit {
    from { transform: rotate(0deg) translateX(${({ $orbitSize }) => $orbitSize / 2}px) rotate(0deg); }
    to { transform: rotate(360deg) translateX(${({ $orbitSize }) => $orbitSize / 2}px) rotate(-360deg); }
  }
`

export default function LogoOrbit({
  src,
  position = 'bottom-right',
  size = 360,
  centerSize = 100,
  orbitCount = 3,
  ringCount = 3,
  color = '233, 30, 99',
  opacity = 0.45,
}: LogoOrbitProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Cleanup handled by React unmount
  }, [])

  const satelliteSize = centerSize * 0.25

  return (
    <Container ref={containerRef} $position={position} $size={size} $zindex={0}>
      {/* Pulse rings */}
      {Array.from({ length: ringCount }).map((_, i) => (
        <Ring
          key={`ring-${i}`}
          $size={centerSize + (i + 1) * 40}
          $delay={i * 0.5}
          $color={color}
        />
      ))}

      {/* Orbit lines and satellites */}
      {Array.from({ length: orbitCount }).map((_, i) => {
        const orbitSize = centerSize + 60 + i * 50
        const duration = 15 + i * 5
        const initialAngle = (i * 360) / orbitCount
        const delay = -(initialAngle / 360) * duration
        return (
          <div key={`orbit-${i}`}>
            <OrbitLine $size={orbitSize} $duration={duration} />
            <Satellite
              src={src}
              $orbitSize={orbitSize}
              $duration={duration}
              $delay={delay}
              $size={satelliteSize}
              style={{ marginTop: -satelliteSize / 2, marginLeft: -satelliteSize / 2 }}
            />
          </div>
        )
      })}

      {/* Center logo */}
      {centerSize > 0 && <CenterLogo src={src} $size={centerSize} $opacity={opacity} $color={color} />}
    </Container>
  )
}
